'use client';

import { useState, useMemo } from 'react';
import type { FiltersSidebarProps } from '../types';
import { useClientConfig } from '@/lib/clients/ClientContext';
import { highlightTokens, getQueryTokens } from '../highlightTokens';
import { CloseIcon } from '../icons';

function CheckboxIcon({ checked }: { checked: boolean }) {
  if (checked) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 mt-px text-brand">
        <rect x="0.5" y="0.5" width="15" height="15" rx="2.5" fill="currentColor" stroke="currentColor" />
        <path d="M12 5L6.5 10.5L4 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0 mt-px">
      <rect x="0.5" y="0.5" width="15" height="15" rx="2.5" stroke="#CDCDCD" />
    </svg>
  );
}

/** Removable pill chip — the price chip's visual language, reused for every
 *  active filter in the "Ενεργά φίλτρα" block. */
function FilterChip({ label, ariaLabel, onRemove }: { label: string; ariaLabel: string; onRemove?: () => void }) {
  return (
    <button
      type="button"
      onClick={onRemove}
      aria-label={ariaLabel}
      className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand/10 text-brand text-[13px] font-gotham font-medium hover:bg-brand/15 transition-colors cursor-pointer max-w-full"
    >
      <span className="truncate">{label}</span>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden className="flex-shrink-0">
        <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </button>
  );
}

export default function FiltersSidebar({
  groups,
  selectedItems = [],
  onFilterToggle,
  highlightQuery,
  activeChips = [],
  onRemoveChip,
  onClearAllFilters,
  onCollapse,
}: FiltersSidebarProps) {
  const clientConfig = useClientConfig();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const queryTokens = useMemo(() => getQueryTokens(highlightQuery), [highlightQuery]);

  const toggleExpand = (title: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  return (
    <div className="flex flex-col items-start w-[280px] h-full overflow-y-auto overscroll-contain custom-scrollbar bg-[#F8F8F8] pt-6 pr-9 pb-6 pl-6 gap-6">
      {/* Title — in the slide-over (onCollapse set) it shows an ✕ to close
          (was a ‹ chevron; an ✕ says "close" without thinking — founder,
          2026-11-03) and uses the larger 16px heading; the persistent sidebar
          keeps its plain 14px title. */}
      <div className="flex justify-between items-center self-stretch">
        <h3 className={`font-gotham font-bold text-text-primary leading-[28px] ${onCollapse ? 'text-[16px]' : 'text-[14px]'}`}>
          {clientConfig.ui.strings.filtersTitle}
        </h3>
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            aria-label="Κλείσιμο φίλτρων"
            className="-mr-2 w-9 h-9 flex items-center justify-center text-text-primary hover:text-brand transition-colors cursor-pointer"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Active-filters block — every selected filter as a removable chip,
          with a filters-only "clear all" flowing INLINE after the last chip
          (Skroutz-style). No sub-heading: it would double up with the
          "Φίλτρα" title directly above, and the 220px usable width can't
          fit a heading beside the clear-all link without wrapping (founder
          feedback 2026-07-21). Guarantees every active filter stays visible
          even when its checkbox is behind a "show more" fold. */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 self-stretch pb-4 border-b border-[#D8D8D8]">
          {activeChips.map(chip => (
            <FilterChip
              key={chip.key}
              label={chip.label}
              ariaLabel={`Αφαίρεση φίλτρου: ${chip.label}`}
              onRemove={onRemoveChip && (() => onRemoveChip(chip.key))}
            />
          ))}
          {onClearAllFilters && (
            <button
              type="button"
              onClick={onClearAllFilters}
              className="text-[13px] font-gotham font-medium text-brand hover:underline cursor-pointer whitespace-nowrap px-1 py-1.5"
            >
              Καθαρισμός όλων
            </button>
          )}
        </div>
      )}

      {/* Filter groups */}
      <div className="flex flex-col items-start self-stretch">
        {groups.map((group, groupIdx) => {
          const isExpanded = expandedGroups.has(group.title);
          const maxVisible = group.maxVisible;
          const hasMore = maxVisible != null && group.items.length > maxVisible;
          const visibleItems = hasMore && !isExpanded
            ? group.items.slice(0, maxVisible)
            : group.items;
          const hiddenCount = hasMore ? group.items.length - maxVisible : 0;

          return (
            <div
              key={group.title}
              className={`flex flex-col items-start gap-2 self-stretch pb-4 border-b border-[#D8D8D8]${groupIdx > 0 ? ' pt-4' : ''}`}
            >
              {/* Group title */}
              <h4 className="text-[14px] font-gotham font-medium text-text-primary leading-[28px] self-stretch">
                {group.title}
              </h4>

              {/* Items */}
              <div className="flex flex-col items-start self-stretch">
                {visibleItems.map(({ name, count, key }) => {
                  const selectionKey = key ?? name;
                  const isSelected = selectedItems.includes(selectionKey);
                  return (
                    <button
                      key={selectionKey}
                      onClick={() => onFilterToggle(group.title, selectionKey)}
                      className="flex min-h-8 py-2 justify-between items-start self-stretch cursor-pointer transition-colors text-left"
                    >
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        <CheckboxIcon checked={isSelected} />
                        <span className={`text-[14px] font-gotham leading-[18px] break-words min-w-0 ${
                          isSelected ? 'font-medium text-brand' : 'font-normal text-text-primary'
                        }`}>
                          {!isSelected && queryTokens.length > 0
                            ? highlightTokens(name, queryTokens)
                            : name}
                        </span>
                      </div>
                      <span className="text-[14px] font-gotham font-normal text-[#6A7282] leading-[18px] flex-shrink-0 ml-2">
                        {count}
                      </span>
                    </button>
                  );
                })}

                {/* Show more / less toggle */}
                {hasMore && (
                  <button
                    onClick={() => toggleExpand(group.title)}
                    className="text-[13px] font-gotham font-medium text-brand mt-1 cursor-pointer hover:underline"
                  >
                    {isExpanded ? clientConfig.ui.strings.showLess : clientConfig.ui.strings.showMore(hiddenCount)}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
